import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Container from '/@/components/container';
import CropMonitoringHeader from '/@/components/crop-monitoring/header';
import CropMonitoringTable from '/@/components/crop-monitoring/table';
import FrostRiskReco from '/@/components/frost-risk-reco';
import { useCropMonitorings } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import { getAppInstance } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';

const includes = (cm: CropMonitoring) => {
    const cropData = getCropMonitoringData(cm);
    return [cropData?.productName, cm.fieldName, cm.created, cm.plantingDate];
};

const filterByPhenologicalState = (cm: CropMonitoring, state: string): boolean => {
    const cropData = getCropMonitoringData(cm);

    switch (state) {
        case 'fallow':
            return !cm.plantingDate || new Date() <= new Date(cm.plantingDate);
        case 'sowing':
            return (
                (!cropData?.state?.phenology || cropData?.state.phenology === null) &&
                (!cropData?.state?.vegetativeStage || cropData?.state.vegetativeStage === null) &&
                new Date() > new Date(cm.plantingDate)
            );
        case 'emergence':
            return cropData?.state?.phenology === 'emergence' && !cropData?.state.vegetativeStage;
        case 'vegetative':
            return (
                cropData?.state?.phenology === 'emergence' &&
                cropData?.state.vegetativeStage &&
                cropData?.state.vegetativeStage >= 1 &&
                cropData?.state.vegetativeStage <= 18
            );
        case 'flowering':
            return cropData?.state?.phenology === 'flowering';
        case 'maturity':
            return cropData?.state?.phenology === 'maturity';
        case 'harvested':
            return cropData?.state?.phenology === 'harvested';
        default:
            return true;
    }
};

const CropMonitoringView = () => {
    const [cropMonitorings, setCropMonitorings] = useState<CropMonitoring[]>([]);
    const appInstance = useSelector(getAppInstance);
    const [showFilter, setShowFilter] = useState(true);
    const [selected, setSelected] = useState<CropMonitoring[]>([]);
    const [openSnack, setOpenSnack] = useState(false);
    const [filterCropMonitorings, setFilterCropMonitorings] = useState<Record<string, any>>({});
    const [filteredCropMonitorings, setFilteredCropMonitorings] = useState<CropMonitoring[]>([]);
    const { data: cropMonitoringsHook, isValidating: loading } = useCropMonitorings({
        pageSize: 10000,
        page: 1,
    });

    useEffect(() => {
        if (cropMonitoringsHook?.results) {
            setCropMonitorings(cropMonitoringsHook.results);
        }
    }, [cropMonitoringsHook]);

    useEffect(() => {
        setFilteredCropMonitorings(
            cropMonitorings.filter((cm: CropMonitoring) => {
                const filter = JSON.parse(JSON.stringify(filterCropMonitorings));
                const search = filter.search;
                delete filter['search'];

                const product = filter.product;
                delete filter['product'];

                const fieldName = filter.fieldName;
                delete filter['fieldName'];

                const phenologicalState = filter.phenologicalState;
                delete filter['phenologicalState'];

                const created = filter.created;
                delete filter['created'];

                if (search) {
                    if (
                        !includes(cm).some(
                            (text) => typeof text === 'string' && text.toLowerCase().includes(search.toLowerCase())
                        )
                    )
                        return false;
                }

                const cropData = getCropMonitoringData(cm);
                if (product === '') {
                    if (cropData?.productName !== undefined) return false;
                } else if (product !== undefined && (!cropData?.productName || cropData?.productName !== product)) {
                    return false;
                }

                if (fieldName === '') {
                    if (cm.fieldName !== undefined) return false;
                } else if (fieldName !== undefined && (!cm.fieldName || cm.fieldName !== fieldName)) {
                    return false;
                }

                if (phenologicalState && !filterByPhenologicalState(cm, phenologicalState)) {
                    return false;
                }

                if (created === '') {
                    if (cm.created !== undefined) return false;
                } else if (
                    created !== undefined &&
                    (!cm.created || cm.created.split('T')?.[0]?.replaceAll('-', '/') !== created)
                ) {
                    return false;
                }

                if (Object.keys(filter).filter((key) => filter[key] !== undefined).length > 0) {
                    return Object.keys(filter).every(
                        (key) => filter[key] === undefined || (cm as any)[key] === filter[key]
                    );
                }
                return true;
            })
        );
    }, [cropMonitorings, filterCropMonitorings]);

    return (
        <Container>
            <CropMonitoringHeader
                cropMonitorings={cropMonitorings}
                filterCropMonitorings={filterCropMonitorings}
                setFilterCropMonitorings={setFilterCropMonitorings}
                showFilter={showFilter}
                selected={selected}
                setShowFilter={setShowFilter}
                setSelected={setSelected}
                setOpenSnack={setOpenSnack}
            />

            <CropMonitoringTable
                loading={loading}
                cropMonitorings={filteredCropMonitorings}
                filterCropMonitorings={filterCropMonitorings}
            />
            {appInstance.enableFrostRisk && <FrostRiskReco view='expanded' />}
        </Container>
    );
};

export default CropMonitoringView;
